import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion/Accordion";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection/AccordionSection";
import GridContainer from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridItem, Typography } from "@insite/mobius/index";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getInvoiceListOrderHistoryHandler from "@tKgpco/Handlers/CustomInvoiceListOrderHistoryHandler";
import InvoiceDetailPageTypeLink from "@tKgpco/Overrides/Components/InvoiceDetailPageTypeLink";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import React, { FC, useCallback, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        isLoading: state.KGPState.customInvoiceHistoryOrderList.isLoading,
        invoiceList: state.KGPState.customInvoiceHistoryOrderList.invoiceList,
    };
};
const mapDispatchToProps = {
    getInvoiceListOrderHistoryHandler,
};

export interface ProductSearchScoreDataStyles {
    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
    accordionSectionfalse?: AccordionPresentationProps;
    poLineTitleGridItem?: GridItemProps;
    invoiceListTitleGridItem?: GridItemProps;
    centeringWrapper?: InjectableCss;
    loadingSpinner?: LoadingSpinnerProps;
    invoiceIdElement?: InjectableCss;
}

export const invoiceListOrderStyles: ProductSearchScoreDataStyles = {
    accordionSection: {
        panelProps: {
            css: css`
                > div {
                    border-bottom: 1px solid lightgrey;
                    padding-bottom: 15px;
                    margin-bottom: 15px;
                    &:last-of-type {
                        border: none;
                        margin-bottom: 0;
                    }
                }
            `,
        },
    },
    accordion: {
        css: css`
            @media print {
                button {
                    display: none !important;
                }
            }
        `,
    },
    accordionSectionfalse: {
        css: css`
            @media print {
                button {
                    display: -webkit-flex !important;
                    font-size: 14px !important;
                    align-items: center;
                }
                & div {
                    display: inline-flex !important;
                }
            }
        `,
    },
    poLineTitleGridItem: {
        width: 8,
        css: css`
            font-weight: 600;
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
        `,
    },
    invoiceListTitleGridItem: {
        width: [4, 4, 2, 2, 2],
        css: css`
            font-weight: 600;
            padding-right: 0;
        `,
    },

    centeringWrapper: {
        css: css`
            display: flex;
            height: 150px;
            justify-content: center;
            align-items: center;
        `,
    },
};

const styles = invoiceListOrderStyles;

const InvoiceListOrderHistory: FC<Props> = ({ invoiceList, isLoading, getInvoiceListOrderHistoryHandler }) => {
    const { value: order } = useContext(OrderStateContext);
    const toasterContext = useContext(ToasterContext);
    const [handleAccordian, setHandleAccordian] = useState(false);

    const toggleAccordion = useCallback(
        expanded => {
            setHandleAccordian(!handleAccordian);
            if (order && order?.id && !isLoading && expanded) {
                setHandleAccordian(true);
                getInvoiceListOrderHistoryHandler({
                    orderId: order.id,
                    customerNumber: order.customerNumber,
                    onError: (error: any) => {
                        if (error) {
                            toasterContext.addToast({ body: translate(error.toString()), messageType: "danger" });
                        } else {
                            toasterContext.addToast({
                                body: translate("Something went wrong!"),
                                messageType: "danger",
                            });
                        }
                    },
                });
            }
        },
        [order?.id, invoiceList],
    );

    return (
        <Accordion
            {...(handleAccordian ? { ...styles.accordionSectionfalse } : { ...styles.accordion })}
            headingLevel={3}
        >
            <ManagedAccordionSection
                title={
                    <GridContainer>
                        <GridItem {...styles.invoiceListTitleGridItem}>{translate("Invoices")}</GridItem>
                        <GridItem {...styles.invoiceListTitleGridItem}>{translate("Invoice Date")}</GridItem>
                        <GridItem {...styles.invoiceListTitleGridItem}>{translate("Order Lines")}</GridItem>
                        <GridItem {...styles.invoiceListTitleGridItem}>{translate("PO Lines")}</GridItem>
                        <GridItem {...styles.invoiceListTitleGridItem}>{translate("Status")}</GridItem>
                        <GridItem {...styles.invoiceListTitleGridItem}>{translate("Invoice Total")}</GridItem>
                    </GridContainer>
                }
                initialExpanded={false}
                onTogglePanel={expanded => toggleAccordion(expanded)}
                {...styles.accordionSection}
            >
                {isLoading ? (
                    <StyledWrapper {...styles.centeringWrapper}>
                        <LoadingSpinner {...styles.loadingSpinner} />
                    </StyledWrapper>
                ) : invoiceList && invoiceList.length > 0 ? (
                    invoiceList?.map((item, id) => {
                        return (
                            <GridContainer key={`${id}-GridContainer`}>
                                <GridItem key={`${id}-GridItem`} {...styles.invoiceListTitleGridItem}>
                                    <InvoiceDetailPageTypeLink
                                        title={item.invoiceNo}
                                        invoiceNumber={item.invoiceNo}
                                        customerNumber={item.customerNumber}
                                    />
                                </GridItem>
                                <GridItem key={`${id}-invoiceDate`} {...styles.invoiceListTitleGridItem}>
                                    <LocalizedDateTime
                                        dateTime={new Date(item.invoiceDate)}
                                        options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                    />
                                </GridItem>
                                <GridItem key={`${id}-orderlines`} {...styles.invoiceListTitleGridItem}>
                                    {item.orderLines}
                                </GridItem>
                                <GridItem key={`${id}-poLines`} {...styles.invoiceListTitleGridItem}>
                                    {item.poLines}
                                </GridItem>
                                <GridItem key={`${id}-status`} {...styles.invoiceListTitleGridItem}>
                                    {item.status}
                                </GridItem>
                                <GridItem key={`${id}-invoiceTotal`} {...styles.invoiceListTitleGridItem}>
                                    {`${item.invoiceTotal}`}
                                </GridItem>
                            </GridContainer>
                        );
                    })
                ) : (
                    invoiceList &&
                    invoiceList.length === 0 && (
                        <Typography as="p">{siteMessage("No_invoices_available_for_order")}</Typography>
                    )
                )}
            </ManagedAccordionSection>
        </Accordion>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoiceListOrderHistory),
    definition: {
        displayName: "InvoiceListOrderHistory",
        allowedContexts: ["OrderDetailsPage"],
        group: "Order Details",
    },
};

export default widgetModule;
